import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Chip, CircularProgress, Alert, Snackbar, Paper,
} from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { hashHistory } from 'react-router';
import {
  getProductTemplateCatalog,
  cloneProductTemplate,
} from '../../../../lib/requests.es6.js';

var NAVY = '#173E4B';
var AMBER = '#fba92c';

var CATEGORY_COLORS = {
  'Funeral & Last Expense': { bg: '#FFF7ED', text: '#C2410C', border: '#FED7AA' },
  'Credit Life & Embedded': { bg: '#EFF6FF', text: '#1D4ED8', border: '#BFDBFE' },
  'Health & Medical Cash': { bg: '#F0FDF4', text: '#15803D', border: '#BBF7D0' },
};

function categoryStyle(category) {
  var c = CATEGORY_COLORS[category] || { bg: '#F5F3FF', text: '#6D28D9', border: '#DDD6FE' };
  return {
    height: 22, fontSize: 11, fontWeight: 600,
    bgcolor: c.bg, color: c.text, border: '1px solid ' + c.border,
  };
}

function CountPill(props) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 52 }}>
      <Typography sx={{ fontSize: 17, fontWeight: 700, color: NAVY, lineHeight: 1 }}>
        {props.value}
      </Typography>
      <Typography sx={{ fontSize: 10, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', mt: 0.4 }}>
        {props.label}
      </Typography>
    </Box>
  );
}

function TemplateCard(props) {
  var e = props.entry;
  var busy = props.cloningKey === e.key;
  var disabled = props.cloningKey && !busy;
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid #E5E7EB', borderRadius: 2, p: 2.25,
        display: 'flex', flexDirection: 'column', gap: 1.25, height: '100%',
        transition: 'border-color .15s, box-shadow .15s',
        '&:hover': { borderColor: AMBER, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: NAVY }}>{e.name}</Typography>
        <Chip label={e.category} sx={categoryStyle(e.category)} />
      </Box>

      <Typography sx={{ fontSize: 12.5, color: '#4B5563', lineHeight: 1.5, flexGrow: 1 }}>
        {e.description}
      </Typography>

      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        {(e.tags || []).slice(0, 5).map(function(t) {
          return (
            <Chip key={t} label={t} size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#F3F4F6', color: '#6B7280' }} />
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, py: 1, borderTop: '1px solid #F3F4F6', borderBottom: '1px solid #F3F4F6' }}>
        <CountPill value={e.package_count} label="Tiers" />
        <CountPill value={e.benefit_count} label="Benefits" />
        <CountPill value={e.add_on_count} label="Add-ons" />
        <CountPill value={e.rating_node_count} label="Rating" />
      </Box>

      <Button
        variant="contained"
        size="small"
        disabled={disabled || busy}
        onClick={function() { props.onUse(e.key); }}
        endIcon={busy ? null : <ArrowForward />}
        sx={{ textTransform: 'none', bgcolor: NAVY, alignSelf: 'flex-start', '&:hover': { bgcolor: '#0f2c36' } }}
      >
        {busy ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Use this template'}
      </Button>
    </Paper>
  );
}

// Fetches the archetype catalog and renders the cards. On "Use this template"
// it clones the archetype into a live partner product (backend materializes the
// rating nodes/tables), calls onCloned() (e.g. to close a host dialog), and
// navigates to the new product editor. Shared by the Start-from-Template dialog
// and the Templates module tab.
export default function TemplateCatalogGrid(props) {
  var onCloned = props.onCloned;

  var [entries, setEntries] = useState([]);
  var [loading, setLoading] = useState(false);
  var [error, setError] = useState(null);
  var [cloningKey, setCloningKey] = useState(null);
  var [cloneError, setCloneError] = useState(null);

  useEffect(function() {
    setLoading(true);
    setError(null);
    getProductTemplateCatalog()
      .then(function(res) { setEntries(res.data || []); })
      .catch(function(err) { setError((err && err.message) || 'Failed to load templates'); })
      .then(function() { setLoading(false); });
  }, []);

  var handleUse = function(key) {
    setCloningKey(key);
    setCloneError(null);
    cloneProductTemplate(key)
      .then(function(res) {
        var product = res.data || {};
        setCloningKey(null);
        if (onCloned) onCloned(product);
        if (product.guid) {
          hashHistory.push('admin/view-generic-product/' + product.guid);
        }
      })
      .catch(function(err) {
        setCloningKey(null);
        var msg = (err && err.response && err.response.data && err.response.data.message)
          || (err && err.message) || 'Failed to create product from template';
        setCloneError(msg);
      });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: NAVY }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
        }}
      >
        {entries.map(function(e) {
          return <TemplateCard key={e.key} entry={e} cloningKey={cloningKey} onUse={handleUse} />;
        })}
      </Box>

      <Snackbar
        open={!!cloneError}
        autoHideDuration={6000}
        onClose={function() { setCloneError(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={function() { setCloneError(null); }}>{cloneError}</Alert>
      </Snackbar>
    </Box>
  );
}
